import { Pageable } from 'shared/table/models/Pageable';
import { MovimientosRubro } from '../models/MovimientosRubro';
import { CardReport } from '../models/CardReport';
import { Movimiento } from '../models/Movimiento';
import { createContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCoreService } from 'core/services/useCoreService';
import { SearchCardsTotalsError } from '../models/errors/SearchCardsTotalsError';
import {
  CARDS_AUTHORIZATIONS_PATH,
  SEARCH_CARD_TOTALS_BY_PERIOD_PATH,
  SEARCH_CARD_TOTALS_PATH,
} from '../routes';
import { Rubro } from '../models/Rubro';
import { CardReportRequest } from 'core/models/CardReportRequest';
import { formatPeriodo } from 'shared/utils';
import { ACCOUNT_TOTALS_BY_ACCOUNT_PATH } from 'core/cuenta/routes';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface ICardTotalsReportContext {
  groupByCategory(rubroId?: number): void;
  goToCardAuthorizations(): void;
  goToAccountMovements(): void;
  goBack(): void;
  cardData: CardReport;
  rubros: Rubro[];
  movimientos: Pageable<Movimiento>;
  movimientosRubro: MovimientosRubro[];
  cardNumber: string;
  searchParams: URLSearchParams;
}

export const CardTotalsReportContext = createContext<ICardTotalsReportContext>({
  groupByCategory: () => {},
  goToCardAuthorizations: () => {},
  goToAccountMovements: () => {},
  goBack: () => {},
  cardData: null,
  rubros: null,
  movimientos: null,
  movimientosRubro: null,
  cardNumber: null,
  searchParams: null,
});

export const CardTotalsReportProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const coreService = useCoreService();
  const { state, pathname } = useLocation();
  const [cardNumber] = useState(state && state['tarjeta']);
  const [cardData, setCardDataRequest] = useDataFetcher<CardReport>(null);
  const [movimientosRubro, setMovimientosRubro] = useState<MovimientosRubro[]>(null);
  const [goBackPath, setGoBackPath] = useState<string>(SEARCH_CARD_TOTALS_PATH);
  const { addNotification } = useNotifications();

  useEffect(() => {
    const request = new CardReportRequest({
      tarjeta: state['tarjeta'],
      selectdate: formatPeriodo(searchParams),
    });

    let path = SEARCH_CARD_TOTALS_PATH;
    if (searchParams.has('mes') && searchParams.has('anio')) {
      path = SEARCH_CARD_TOTALS_BY_PERIOD_PATH;
    }
    setGoBackPath(path);

    setCardDataRequest(() =>
      coreService.getTarjeta(request).then(
        (response) => {
          return response.body;
        },
        (err) => {
          const message = new SearchCardsTotalsError(err.error).message;
          addNotification(message, 'error');
          navigate(path);
          return null;
        },
      ),
    );
  }, [
    cardNumber,
    state,
    searchParams,
    addNotification,
    coreService,
    navigate,
    setCardDataRequest,
  ]);

  const contextValue = {
    cardData: cardData.data,
    movimientos: cardData.data?.movimientos,
    movimientosRubro,
    cardNumber,
    searchParams,
    rubros: cardData.data?.rubros,
    groupByCategory: (rubroId?: number) => {
      coreService
        .getCardReportByCategory(
          new CardReportRequest({
            tarjeta: cardNumber,
            selectdate: formatPeriodo(searchParams),
            rubroId: rubroId,
          }),
        )
        .then(
          (response) => {
            setMovimientosRubro(response.body.movimientosRubro);
          },
          (err) => {
            const message = new SearchCardsTotalsError(err.error).message;
            addNotification(message, 'error');
            navigate(goBackPath);
          },
        );
    },
    goToCardAuthorizations: () => {
      navigate(
        {
          pathname: CARDS_AUTHORIZATIONS_PATH.replace(
            ':cardNumber',
            cardData.data.numeroTarjeta,
          ),
          search: searchParams.toString(),
        },
        {
          state: {
            tarjeta: cardNumber,
            fromPath: pathname,
          },
        },
      );
    },
    goToAccountMovements: () => {
      navigate(
        `${ACCOUNT_TOTALS_BY_ACCOUNT_PATH.replace(
          ':accountNumber',
          cardData.data.cuentaOrigen,
        )}?${searchParams.toString()}`,
        {
          state: {
            tarjeta: cardNumber,
            fromPath: pathname,
          },
        },
      );
    },
    goBack: () => {
      if (movimientosRubro) {
        setMovimientosRubro(null);
      } else if (state && (state as any)['fromAuthSearch']) {
        navigate(
          {
            pathname: state['fromPath'],
          },
          {
            state,
          },
        );
      } else {
        navigate(
          {
            pathname: goBackPath,
          },
          {
            state: {
              tarjeta: cardNumber,
            },
          },
        );
      }
    },
  };
  return (
    <CardTotalsReportContext.Provider value={contextValue}>
      <LoadingSpinner isLoading={cardData.isLoading}>
        {cardData.data && children}
      </LoadingSpinner>
    </CardTotalsReportContext.Provider>
  );
};
